import { useState } from "react";
import Sheet from "./Sheet";
import Icon from "./Icons";
import { Switch, RadioList, Select, RangeSlider } from "./controls";
import { SORTS, facets as computeFacets, activeFilterCount, hasPrice, priceLabel } from "./shopData";

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
const czk = (n) => `${n.toLocaleString("cs-CZ")} Kč`;

// Category tabs, a sort menu, and a filter drawer with live counts.
export default function Filters({ products, filters: f, onChange, total }) {
  const [open, setOpen] = useState(false);
  const fc = computeFacets(products, f);
  const set = (patch) => onChange({ ...f, ...patch });
  const n = activeFilterCount(f);
  const clearAll = () => set({ sizes: [], colours: [], price: { min: null, max: null }, inStock: false, sale: false });
  const sortOptions = SORTS.map((s) => ({ value: s.id, label: s.label }));
  const pieces = `${total} ${total === 1 ? "piece" : "pieces"}`;

  const chips = [
    ...f.sizes.map((v) => ({ key: `s${v}`, label: `Size ${v}`, off: () => set({ sizes: toggle(f.sizes, v) }) })),
    ...f.colours.map((v) => ({ key: `c${v}`, label: v, off: () => set({ colours: toggle(f.colours, v) }) })),
    hasPrice(f.price) && { key: "price", label: priceLabel(f.price), off: () => set({ price: { min: null, max: null } }) },
    f.inStock && { key: "stock", label: "In stock", off: () => set({ inStock: false }) },
    f.sale && { key: "sale", label: "On sale", off: () => set({ sale: false }) },
  ].filter(Boolean);

  return (
    <div className="sh-filters">
      <div className="sh-toolbar">
        {fc.categories.length > 1 ? (
          <nav className="sh-tabs" aria-label="Categories">
            <button className={!f.category ? "on" : ""} onClick={() => set({ category: "" })}>
              All
            </button>
            {fc.categories.map((c) => (
              <button key={c.slug} className={f.category === c.slug ? "on" : ""} onClick={() => set({ category: f.category === c.slug ? "" : c.slug })}>
                {c.name}
              </button>
            ))}
          </nav>
        ) : (
          <span className="sh-count">{pieces}</span>
        )}
        <div className="sh-toolbar-end">
          <button className="sh-toolbtn" onClick={() => setOpen(true)} aria-haspopup="dialog">
            <Icon name="filter" size={18} /> Filter{n > 0 && <span className="sh-pill">{n}</span>}
          </button>
          <Select variant="inline" align="end" label="Sort by" value={f.sort} onChange={(sort) => set({ sort })} options={sortOptions} />
        </div>
      </div>

      {(chips.length > 0 || f.q) && (
        <div className="sh-chips">
          {f.q && (
            <button className="sh-chip" onClick={() => set({ q: "" })}>
              “{f.q}” <Icon name="close" size={12} />
            </button>
          )}
          {chips.map((c) => (
            <button key={c.key} className="sh-chip" onClick={c.off}>
              {c.label} <Icon name="close" size={12} />
            </button>
          ))}
          {chips.length > 1 && (
            <button className="sh-textlink" onClick={clearAll}>
              Clear all
            </button>
          )}
          <span className="sh-count">{pieces}</span>
        </div>
      )}

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title="Filter"
        badge={n || null}
        footer={
          <>
            <button className="sh-fp-foot-clear" onClick={clearAll} disabled={!n}>
              Clear all
            </button>
            <button className="sh-btn" onClick={() => setOpen(false)}>
              Show {pieces}
            </button>
          </>
        }
      >
        <Group name="Sort" summary={SORTS.find((s) => s.id === f.sort)?.label} open={false}>
          <RadioList name="sh-sort" value={f.sort} onChange={(sort) => set({ sort })} options={sortOptions} aria-label="Sort by" />
        </Group>

        {fc.sizes.length > 0 && (
          <Group name="Size" summary={f.sizes.join(", ")}>
            <div className="sh-sizegrid">
              {fc.sizes.map((s) => {
                const on = f.sizes.includes(s.value);
                return (
                  <button
                    key={s.value}
                    type="button"
                    className={`sh-size${on ? " on" : ""}${!s.count ? " out" : ""}`}
                    disabled={!s.count && !on}
                    aria-pressed={on}
                    aria-label={`Size ${s.value}${s.count ? `, ${s.count} ${s.count === 1 ? "piece" : "pieces"}` : ", none in stock"}`}
                    onClick={() => set({ sizes: toggle(f.sizes, s.value) })}
                  >
                    {s.value}
                  </button>
                );
              })}
            </div>
          </Group>
        )}

        {fc.colours.length > 0 && (
          <Group name="Colour" summary={f.colours.join(", ")}>
            <div className="sh-fp-colours">
              {fc.colours.map((c) => {
                const on = f.colours.includes(c.value);
                return (
                  <label key={c.value} className={`sh-fp-tile${on ? " on" : ""}${!c.count ? " is-empty" : ""}`}>
                    <input type="checkbox" className="sh-ctl-input" checked={on} onChange={() => set({ colours: toggle(f.colours, c.value) })} />
                    <i className="sh-fp-swatch" style={c.hex ? { background: c.hex } : undefined} />
                    <span className="sh-fp-tilename">{c.value}</span>
                    <em className="sh-ctl-count">{c.count}</em>
                  </label>
                );
              })}
            </div>
          </Group>
        )}

        {fc.price && (
          <Group name="Price" summary={hasPrice(f.price) ? priceLabel(f.price) : ""}>
            <div className="sh-fp-range">
              <RangeSlider
                min={fc.price.min}
                max={fc.price.max}
                step={fc.price.step}
                value={[f.price.min ?? fc.price.min, f.price.max ?? fc.price.max]}
                bars={fc.price.bars}
                format={czk}
                labels={["Lowest price", "Highest price"]}
                captions={["From", "To"]}
                onChange={([lo, hi]) => set({ price: { min: lo <= fc.price.min ? null : lo, max: hi >= fc.price.max ? null : hi } })}
              />
            </div>
          </Group>
        )}

        <Group name="Availability" summary={[f.inStock && "In stock", f.sale && "On sale"].filter(Boolean).join(", ")}>
          <div className="sh-fp-switches">
            <Switch checked={f.inStock} onChange={() => set({ inStock: !f.inStock })} count={fc.inStock}>
              In stock only
            </Switch>
            {(fc.sale > 0 || f.sale) && (
              <Switch checked={f.sale} onChange={() => set({ sale: !f.sale })} count={fc.sale}>
                On sale
              </Switch>
            )}
          </div>
        </Group>
      </Sheet>
    </div>
  );
}

// A collapsible group: its name on the left, what's picked on the right.
function Group({ name, summary, open: startOpen = true, children }) {
  const [open, setOpen] = useState(startOpen);
  return (
    <section className={`sh-fp-group${open ? " open" : ""}`}>
      <button type="button" className="sh-fp-trigger" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="sh-fp-name">{name}</span>
        <span className="sh-fp-summary">{summary}</span>
        <Icon name="down" size={16} />
      </button>
      <div className="sh-fp-collapse">
        <div className="sh-fp-inner">
          <div className="sh-fp-body">{children}</div>
        </div>
      </div>
    </section>
  );
}
